import {
  FlexBox,
  getDefaultOutageExperience,
  OutageExperienceCategory,
  outageExperienceOptions,
  SelectableCard,
} from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { AppointmentSurvey } from '../../../../data-access/appointmentProposals';

type OutageExperienceSectionProps = {
  defaultValues?: AppointmentSurvey['outageExperience'];
  formId?: string;
  onSubmit: (data: AppointmentSurvey['outageExperience']) => Promise<void>;
};

const OutageExperienceSchema = z.object({
  eventConcerns: z.string().array(),
  homeAppliances: z.string().array(),
  homeSystems: z.string().array(),
  technologyElectronics: z.string().array(),
});

export type OutageExperienceInputs = AppointmentSurvey['outageExperience'];

const categoryLabels: Record<OutageExperienceCategory, string> = {
  eventConcerns: 'Outage event concerns',
  homeAppliances: 'Home appliances',
  homeSystems: 'Home systems',
  technologyElectronics: 'Technology & electronics',
};

const OutageExperienceSection: React.FC<OutageExperienceSectionProps> = ({
  defaultValues,
  formId,
  onSubmit,
}) => {
  const { control, handleSubmit } = useForm<OutageExperienceInputs>({
    resolver: zodResolver(OutageExperienceSchema),
    defaultValues: defaultValues || getDefaultOutageExperience(),
  });

  const toggleSelection = (fieldValue: string[], value: string) =>
    fieldValue.includes(value)
      ? fieldValue.filter((item) => item !== value)
      : [...fieldValue, value];

  const categories: OutageExperienceCategory[] = [
    'eventConcerns',
    'homeAppliances',
    'homeSystems',
    'technologyElectronics',
  ];

  return (
    <Box
      component="form"
      id={formId}
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        width: '100%',
      }}
    >
      {categories.map((category) => (
        <Controller
          key={category}
          name={category}
          control={control}
          render={({ field }) => (
            <FlexBox flexDirection="column" gap={2}>
              <Typography variant="subtitle2">
                {categoryLabels[category]}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: ['1fr', '1fr 1fr', '1fr 1fr 1fr'],
                  gridAutoRows: '1fr',
                  gap: 2,
                }}
              >
                {outageExperienceOptions[category].map((option) => (
                  <SelectableCard
                    key={option.id}
                    selected={field.value.includes(option.id)}
                    onClick={() =>
                      field.onChange(
                        toggleSelection(field.value || [], option.id)
                      )
                    }
                    color={'blue.600'}
                  >
                    {option.icon}
                    <Typography variant="body2" sx={{ textAlign: 'center' }}>
                      {option.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textAlign: 'center' }}
                    >
                      {option.description}
                    </Typography>
                  </SelectableCard>
                ))}
              </Box>
            </FlexBox>
          )}
        />
      ))}
    </Box>
  );
};

export default OutageExperienceSection;
